import { useContext, useEffect } from "react";
import {
  BotFormType,
  Error,
  UseBotNameInput,
  UseMarginForm,
  UseSymbolAndAccountSelectors,
} from "@shared/features/bots/BotForm/common/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { BotData, BotType } from "@shared/types";

interface UseGeneralProps {
  useMarginForm: UseMarginForm;
  useSymbolAndAccountSelectors?: UseSymbolAndAccountSelectors;
  useSignalSymbolAndExchangesSelectors?: UseSymbolAndAccountSelectors;
  useBotNameInput: UseBotNameInput;
  width?: number;
  queryAccountId?: number;
}

const useGeneral = ({
  useMarginForm,
  useSymbolAndAccountSelectors,
  useSignalSymbolAndExchangesSelectors,
  useBotNameInput,
  width,
  queryAccountId,
}: UseGeneralProps) => {
  const {
    botType,
    isPublic,
    addError,
    removeError,
    botGeneralData,
    isEdit,
    botFormType,
    isDuplicate,
    botId,
    isLeverageExchanges,
    setIsLeverageExchanges,
    initialized,
    isChannelConfiguration,
    isBacktesting,
  } = useContext(BotFormContext)!;
  const marginFormData = useMarginForm();
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const isBotEdit = botFormType === BotFormType.EDIT || isBulkEdit;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const symbolAndAccountSelectorsData = useSymbolAndAccountSelectors?.({
    defaultAccountId:
      queryAccountId ??
      ((isEdit || isBacktesting) && !isPublic
        ? (botGeneralData as BotData)?.client_id
        : undefined),
    defaultExchange: isPublic
      ? (botGeneralData as BotData)?.exchange
      : undefined,
    defaultSymbols:
      isEdit || isBacktesting
        ? (botGeneralData as BotData)?.symbols
        : undefined,
    defaultBlacklistSymbols:
      isEdit || isBacktesting
        ? (botGeneralData as BotData)?.blacklist_symbols
        : undefined,
    initialize:
      ((!isEdit && !isBacktesting) || !!botGeneralData) && !isBulkEdit,
    disable: isBotEdit,
  });
  const signalSymbolAndExchangesSelectors =
    useSignalSymbolAndExchangesSelectors?.({});

  const nameInputData = useBotNameInput({
    currentBotId: isPublic || isDuplicate ? undefined : botId,
    initialize: initialized && ((!botId && !isBulkEdit) || isPublic),
  });

  const { accountIsValid, symbolIsValid } =
    symbolAndAccountSelectorsData || signalSymbolAndExchangesSelectors || {};
  const { nameError } = nameInputData;

  const isGeneralError =
    !isChannelConfiguration && (!accountIsValid || !symbolIsValid);
  useEffect(() => {
    if (isGeneralError) addError(Error.GENERAL);
    else removeError(Error.GENERAL);
  }, [isGeneralError]);

  useEffect(() => {
    if (nameError && !isChannelConfiguration) addError(Error.BOT_NAME);
    else removeError(Error.BOT_NAME);
  }, [nameError]);

  const leveragedExchangesSelectorData = {
    shouldDisplay: false,
    title: "Is Leveraged Exchanges",
    value: isLeverageExchanges,
    onChange: setIsLeverageExchanges,
  };

  let numColumns = width && width < 350 ? 1 : 2;
  if (isTradingViewSignalsBot || (isSignalsBot && width && width < 450)) {
    numColumns = 1;
  }

  return {
    numColumns,
    marginFormData,
    symbolAndAccountSelectorsData:
      symbolAndAccountSelectorsData ?? signalSymbolAndExchangesSelectors,
    nameInputData,
    leveragedExchangesSelectorData,
  };
};

export default useGeneral;
